"use client";

import { useEffect, useLayoutEffect, useRef } from "react";

/**
 * Persists vertical scroll across full page reloads (sessionStorage + restore after layout settles).
 * Skips when the URL has a hash so in-page anchors still work.
 */
export function usePageScrollRestore(storageKey: string, enabled = true) {
	const restoredRef = useRef(false);

	useEffect(() => {
		if (!enabled || typeof window === "undefined") return;

		const save = () => {
			sessionStorage.setItem(storageKey, String(Math.round(window.scrollY)));
		};

		window.addEventListener("pagehide", save);
		document.addEventListener("visibilitychange", () => {
			if (document.visibilityState === "hidden") save();
		});

		return () => {
			window.removeEventListener("pagehide", save);
		};
	}, [storageKey, enabled]);

	useLayoutEffect(() => {
		if (!enabled || typeof window === "undefined" || restoredRef.current) return;
		if (window.location.hash) {
			restoredRef.current = true;
			return;
		}

		const raw = sessionStorage.getItem(storageKey);
		if (!raw) {
			restoredRef.current = true;
			return;
		}

		const targetY = Number.parseInt(raw, 10);
		if (!Number.isFinite(targetY) || targetY <= 0) {
			restoredRef.current = true;
			return;
		}

		const restore = () => {
			const maxY = Math.max(
				0,
				document.documentElement.scrollHeight - window.innerHeight,
			);
			window.scrollTo(0, Math.min(targetY, maxY));
		};

		restore();

		let frameId = 0;
		let attempts = 0;
		const maxAttempts = 16;

		const tick = () => {
			restore();
			attempts += 1;
			if (attempts < maxAttempts) {
				frameId = requestAnimationFrame(tick);
			} else {
				restoredRef.current = true;
			}
		};
		frameId = requestAnimationFrame(tick);

		const ro = new ResizeObserver(() => {
			if (!restoredRef.current) restore();
		});
		ro.observe(document.body);

		const stopTimer = window.setTimeout(() => {
			restoredRef.current = true;
			ro.disconnect();
		}, 3000);

		return () => {
			cancelAnimationFrame(frameId);
			ro.disconnect();
			clearTimeout(stopTimer);
		};
	}, [storageKey, enabled]);
}
